import React, { useEffect, useRef } from 'react';

import {
  TextOrderingAnswer,
  TextOrderingUnitType,
} from '../../../models/GameStore';
import { QuickMemoryBlankUnit, SpellingBlankUnit } from '../../Molecules';
import { TextOrderingBlankInfo } from '../MemoryGameMainGroup';

import styles from './GameQuestionPanel.module.css';

export enum TextOrderingGameMode {
  QuickMemory = 'QuickMemory',
  Syllable = 'Syllable',
  Spelling = 'Spelling',
}

type GameQuestionPanelProps = {
  answer: TextOrderingAnswer;
  currentBlankIndex: number;
  spellingPreFilledIndexes: number[][][];
  blankInfos: Record<number, TextOrderingBlankInfo>;
  mode: TextOrderingGameMode;
  useColor: boolean;
  isLearner: boolean;
  isGameStart: boolean;
};

export const GameQuestionPanel: React.FC<GameQuestionPanelProps> = ({
  answer,
  currentBlankIndex = 0,
  spellingPreFilledIndexes,
  blankInfos,
  mode,
  useColor,
  isLearner,
  isGameStart,
}) => {
  const blankRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null); // Changed to single ref

  useEffect(() => {
    const currentRef = blankRefs.current[currentBlankIndex];

    if (currentRef && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const blankRect = currentRef.getBoundingClientRect();

      const scrollNeeded =
        blankRect.top -
        containerRect.top -
        (containerRef.current.offsetHeight - currentRef.offsetHeight) / 2;

      containerRef.current.scrollTo({
        top: containerRef.current.scrollTop + scrollNeeded,
        behavior: 'smooth',
      });
    }
  }, [currentBlankIndex]);

  const calculateHintIndex = (length: number, unitsLength: number) => {
    if (length <= 0) return Math.max(0, length - 1);
    const index = unitsLength % length;
    return index === 0 ? 1 : index;
  };

  // Precompute all blank indices for reference
  const allBlankIndices = answer.words
    .flatMap((word, wordIndex) => {
      // grouping consecutive hints or blanks as an unit group
      // for finding real 'word' (consecutive blanks) in syallable game
      let unitGroup = 0;
      let unitGroupItemIndex = 0; // Track index within the same group

      return word.units.map((unit, unitIndex) => {
        if (unitIndex > 0 && unit.type !== word.units[unitIndex - 1]!.type) {
          unitGroup += 1;
          unitGroupItemIndex = 0; // Reset index for the new group
        }

        const _result = {
          type: unit.type,
          wordIndex,
          unitIndex,
          unitGroup,
          unitGroupItemIndex,
          keyLength: unit.key.length,
        };

        if (unit.type === TextOrderingUnitType.Blank) {
          unitGroupItemIndex += 1; // Increment only for blanks
        }

        return _result;
      });
    })
    .filter((item) => item.type === TextOrderingUnitType.Blank);
  // Render units and split into containers when encountering { type: 'hint', key: '\n' } or word: '\n'
  const renderAnswer = () => {
    const paragraphs: JSX.Element[] = [];
    let currentParagraph: JSX.Element[] = [];
    let isAllAnswered = true;
    let blankCount = 0;
    let redCount = 0;
    let orangeCount = 0;

    const combineBlanks = () => {
      if (blankCount > 0) {
        const blanks = currentParagraph.splice(-blankCount, blankCount);
        if (isAllAnswered) {
          let backgroundColor = '#00A69826';
          if (redCount > 0 || orangeCount > 1) {
            backgroundColor = '#FF595933';
          } else if (orangeCount > 0) {
            backgroundColor = '#F6921E26';
          }

          currentParagraph.push(
            <div
              key={blanks[blanks.length - 1]?.key}
              className={styles.answeredWord}
              style={{ backgroundColor }}
            >
              {blanks}
            </div>,
          );
        } else {
          currentParagraph.push(
            <div
              key={blanks[blanks.length - 1]?.key}
              className={styles.answeredWord}
            >
              {blanks}
            </div>,
          );
        }

        // reset
        isAllAnswered = true;
        blankCount = 0;
        redCount = 0;
        orangeCount = 0;
      }
    };

    const makeParagraph = () => {
      if (currentParagraph.length > 0) {
        combineBlanks();

        // Push the current container into the containers array
        paragraphs.push(
          <div
            key={paragraphs.length}
            className={`${
              mode === TextOrderingGameMode.QuickMemory
                ? styles.quickMemoryWordsContainer
                : styles.spellingWordsContainer
            }`}
          >
            {currentParagraph}
          </div>,
        );

        // Reset current container
        currentParagraph = [];
      }
    };

    answer.words.forEach((word, wordIndex) => {
      if (word.word === '\n') {
        makeParagraph();

        paragraphs.push(
          <div
            key={`linebreak-${paragraphs.length}`}
            style={{
              height: '1rem', // Adjust line height as needed
              width: '100%',
              backgroundColor: 'transparent', // Transparent to avoid background conflict
            }}
          />,
        );
      } else {
        // Process units within the word
        word.units.forEach((unit, unitIndex) => {
          if (unit.type === TextOrderingUnitType.Hint && unit.key === '\n') {
            makeParagraph();
          } else {
            // Add unit to the current container
            if (
              unit.type === TextOrderingUnitType.Hint ||
              unit.type === TextOrderingUnitType.Candidate
            ) {
              combineBlanks();

              currentParagraph.push(
                <span key={`${wordIndex}-${unitIndex}`} className={styles.hint}>
                  {unit.key}
                </span>,
              );
            } else if (unit.type === TextOrderingUnitType.Blank) {
              const length = unit.key.length;
              const hintIndex = calculateHintIndex(
                length,
                word.units.filter((item) => item.key !== ' ').length,
              );

              const globalBlankIndex = allBlankIndices.findIndex(
                (item) =>
                  item.wordIndex === wordIndex && item.unitIndex === unitIndex,
              );
              const isHighlighted = globalBlankIndex === currentBlankIndex;
              const isAnswered =
                blankInfos[globalBlankIndex]?.pickedAnswer != undefined;
              const score = blankInfos[globalBlankIndex]?.score ?? 100;
              const color = blankInfos[globalBlankIndex]?.color;

              if (mode === TextOrderingGameMode.QuickMemory) {
                currentParagraph.push(
                  <QuickMemoryBlankUnit
                    key={`${wordIndex}-${unitIndex}`}
                    unit={unit}
                    isAnswered={isAnswered}
                    isHighlighted={isHighlighted}
                    hintIndex={hintIndex}
                    score={score}
                    color={color}
                    globalBlankIndex={globalBlankIndex}
                    useColor={useColor}
                    blankRefs={blankRefs}
                    isLearner={isLearner}
                  />,
                );
              } else {
                let variant = mode;
                if (mode === TextOrderingGameMode.Syllable) {
                  const unitGroup =
                    allBlankIndices[globalBlankIndex]?.unitGroup;
                  if (
                    allBlankIndices
                      .filter(
                        (i) =>
                          i.wordIndex === wordIndex &&
                          i.unitGroup === unitGroup,
                      )
                      .every((i) => i.keyLength === 1)
                  ) {
                    variant = TextOrderingGameMode.Spelling;
                  }
                }

                // MARK: prefilled process
                const unitGroup = allBlankIndices[globalBlankIndex];
                const wI = unitGroup?.wordIndex;
                const uI = unitGroup?.unitGroup;
                const uGI = unitGroup?.unitGroupItemIndex;
                const isPreFilledBlank =
                  wI !== undefined &&
                  uI !== undefined &&
                  uGI !== undefined &&
                  Array.isArray(spellingPreFilledIndexes[wI]) &&
                  spellingPreFilledIndexes[wI]?.[uI]?.includes(uGI);

                currentParagraph.push(
                  <SpellingBlankUnit
                    key={`${wordIndex}-${unitIndex}`}
                    unit={unit}
                    isAnswered={isAnswered}
                    isHighlighted={isHighlighted}
                    hintIndex={hintIndex}
                    // score={score}
                    color={color}
                    globalBlankIndex={globalBlankIndex}
                    blankRefs={blankRefs}
                    variant={variant}
                    isPreFilled={!!isPreFilledBlank}
                    isLearner={isLearner}
                  />,
                );
                isAllAnswered &&= isAnswered;
                blankCount += 1;
                redCount += score <= 50 ? 1 : 0;
                orangeCount += score <= 95 ? 1 : 0;
              }
            }

            if (unitIndex === word.units.length - 1) {
              combineBlanks();
            }
          }
        });
      }
    });

    makeParagraph();

    return paragraphs;
  };

  return (
    <div
      className={`${styles.container} ${
        isLearner ? styles.learnerContainer : ''
      }  ${isGameStart ? styles.gameStartContainer : ''}`}
      ref={containerRef}
    >
      <div className={styles.center}>
        <div className={styles.contentGroup}>{renderAnswer()}</div>
      </div>
    </div>
  );
};
